import { z } from "zod";
import { articleTypeSchema } from "./article";
import { localizedTextSchema } from "./intake-template";

// TES-484 (Ö-31 + Ö-39, /03) — the women's health ("Kvinnohälsa") surface.
//
// The rule that shapes this DTO: the app shows ONLY topics and content types
// that have published content, and never a hard-coded count. So the server
// projects, per topic, the published courses and articles it can actually
// open; a topic with nothing published is omitted, a type with nothing is an
// empty array (the client hides the label). There are no counts here — the
// client renders "Kurs om mat · Artiklar" from which kinds are present.
//
// Topic membership is editorial and lives server-side (womens-health-topics.ts)
// until the Drive taxonomy carries it in `authorMetadata`.

export const WOMENS_HEALTH_TOPIC_IDS = ["cycle", "pcos", "menopause"] as const;
export const womensHealthTopicIdSchema = z.enum(WOMENS_HEALTH_TOPIC_IDS);
export type WomensHealthTopicId = z.infer<typeof womensHealthTopicIdSchema>;

// A mini-course's overview row (type `mini_lesson`, role `course`) — the key
// opens `MiniCourse`. `area` says whether it is a food or a movement course so
// the client can say "Kurs om mat" / "Kurs om rörelse" without parsing keys.
// `lead` is the row's own first paragraph (plain text) — wireframe 06 shows
// a line of body copy under every item, and the article's ingress is the only
// source of that text that isn't written by the importer or the app.
export const womensHealthCourseSchema = z.object({
  key: z.string(),
  title: localizedTextSchema,
  lead: localizedTextSchema,
  area: z.enum(["nutrition", "training"]),
});
export type WomensHealthCourse = z.infer<typeof womensHealthCourseSchema>;

// A freestanding article (short guide, education card, …) — opens `Article`.
export const womensHealthArticleSchema = z.object({
  key: z.string(),
  title: localizedTextSchema,
  lead: localizedTextSchema,
  type: articleTypeSchema,
});
export type WomensHealthArticle = z.infer<typeof womensHealthArticleSchema>;

export const womensHealthTopicSchema = z.object({
  id: womensHealthTopicIdSchema,
  courses: z.array(womensHealthCourseSchema),
  articles: z.array(womensHealthArticleSchema),
});
export type WomensHealthTopic = z.infer<typeof womensHealthTopicSchema>;

// TES-1457 (Ö-758, decision sheet 2026-10-19) — the approved article
// entrance "Mitt stöd → Kvinnohälsa": article 10 as the overview first, then
// the selectable topics cycle (11), PCOS (12), menopause (13), sleep and
// stress (14), pregnancy/breastfeeding (15) and Ella's focus on women (09).
// Each topic IS one existing knowledge article — the same key and version the
// DTs and need entries open, never a copy. The first three ids are the
// existing `topics` ids, so the client can open that topic's page (where the
// article sits beside its courses and guides); the others open the article.
export const WOMENS_HEALTH_ENTRY_TOPIC_IDS = [
  ...WOMENS_HEALTH_TOPIC_IDS,
  "sleep_stress",
  "pregnancy",
  "ella_focus",
] as const;
export const womensHealthEntryTopicIdSchema = z.enum(
  WOMENS_HEALTH_ENTRY_TOPIC_IDS,
);
export type WomensHealthEntryTopicId = z.infer<
  typeof womensHealthEntryTopicIdSchema
>;

export const womensHealthEntryTopicSchema = z.object({
  id: womensHealthEntryTopicIdSchema,
  article: womensHealthArticleSchema,
});
export type WomensHealthEntryTopic = z.infer<
  typeof womensHealthEntryTopicSchema
>;

export const womensHealthArticleEntrySchema = z.object({
  // Article 10, or null while it is not published.
  overview: womensHealthArticleSchema.nullable(),
  // Only topics whose article is published, in the decided order.
  topics: z.array(womensHealthEntryTopicSchema),
});
export type WomensHealthArticleEntry = z.infer<
  typeof womensHealthArticleEntrySchema
>;

export const womensHealthSurfaceSchema = z.object({
  // Only topics with at least one published item, in canonical order.
  topics: z.array(womensHealthTopicSchema),
  // Every published women's health knowledge article regardless of topic — the
  // destination of "Se all kunskap om kvinnohälsa". Empty ⇒ the link is hidden.
  knowledge: z.array(womensHealthArticleSchema),
  // TES-1457 — additive: app 2.0.11 never reads it and keeps rendering
  // `topics`; an api from before it omits it, and the client then falls back
  // to `topics`.
  articleEntry: womensHealthArticleEntrySchema.optional(),
});
export type WomensHealthSurface = z.infer<typeof womensHealthSurfaceSchema>;
